/**
 * SearchResultsPage — typed search at `/marketplace/search` (STORY-003-008;
 * rebuilt 2026-08 onto the counter-slip direction).
 *
 * The SEARCH-mode sibling of MarketplacePage. Reads `q`, `category`, `market`
 * (repeatable), `in_stock`, `min`, `max`, `sort` from the URL (story shorthand
 * keys — the data hook maps them to the REAL backend param names at the API
 * seam) and calls `GET /marketplace/search?mode=search&q=…` via
 * `useMarketplaceSearch`. The grid, filter sidebar and empty state are the
 * STORY-003-005 components, reused verbatim so the two surfaces cannot drift.
 *
 * WHAT CHANGED VISUALLY. The page put its `<h1>` and its result count on two
 * separate unruled lines with a `text-2xl` heading floating above them, so a
 * search landed with no visible relationship between "what you asked for" and
 * "what came back". Both now sit on one `SlipHeader` rule with the applied-filter
 * row underneath — the same header the browse page uses, which is the point:
 * search is the catalogue with a query, not a different product.
 *
 * Header contract:
 *   - `<h1>` echoes the query — "Результаты поиска: {q}" — via React
 *     text-binding ONLY (AC #8 anti-XSS: never dangerouslySetInnerHTML).
 *   - the count (STORY-003-008 AC #6/#7 superseded in presentation — SCRUM-184
 *     / ADR-050, garden-backlog/ADR-LOG.md): "Примерно 42 товара" for a small
 *     estimate; "Более 1000 товаров" when the (clamped) estimate reaches the
 *     mass threshold. SEARCH NEVER RENDERS A BARE/EXACT COUNT — `search.sql`'s
 *     per-arm candidate cap (LIMIT 40, applied before the outer visibility and
 *     structured filters) means a null `next_cursor` does not prove the
 *     catalogue holds no more matches, so an exact claim would be confidently
 *     wrong. (Browse's sibling label CAN be exact — see MarketplacePage.tsx —
 *     because `BROWSE_SQL` filters entirely inside its capped CTE.) Announced
 *     via `aria-live="polite"` from a node that stays MOUNTED across query
 *     changes (a remount would double-announce).
 *
 * Empty state (AC #5): when the query resolves to zero results, the OOC opt-in
 * hint renders and NO result grid is shown.
 *
 * The query `q` is never submitted empty (the hook's `enabled` gate); an empty /
 * missing `q` renders a prompt to type a query rather than calling the backend
 * (which 422s an empty search). Tokens via the `--g-*` mirror; strings from the
 * `marketplace` namespace. This page is public — no auth header, no JWT, no PII.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal } from "lucide-react";

import { MotifSurface } from "@/components/motion";
import { iconProps } from "@/lib/icons";
import { MOTION } from "@/lib/motion";
import { notify } from "@/lib/notify";
import { useGardenMotion } from "@/lib/use-garden-motion";
import { AppliedFilters } from "@/features/marketplace/AppliedFilters";
import { CategoryFilterSidebar } from "@/features/marketplace/CategoryFilterSidebar";
import { EmptyState } from "@/features/marketplace/EmptyState";
import { LoadError } from "@/features/marketplace/LoadError";
import { FilterSheet } from "@/features/marketplace/MarketplacePage";
import { ProductGrid } from "@/features/marketplace/ProductGrid";
import { SlipHeader } from "@/features/marketplace/SlipHeader";
import type { MarketOption } from "@/features/marketplace/MarketFilterMultiselect";
import type { PriceRangeValue } from "@/features/marketplace/PriceRangeInput";
import { isSortValue, type SortValue } from "@/features/marketplace/SortSelect";
import {
  flattenSearchPages,
  useMarketplaceSearch,
  SEARCH_Q_MAX_LEN,
  type SearchFilters,
  type ProductSearchItem,
} from "@/features/marketplace/useMarketplaceSearch";

/** URL query-string keys (story shorthand — NOT the backend param names). */
const PARAM_Q = "q";
const PARAM_CATEGORY = "category";
const PARAM_MARKET = "market";
const PARAM_IN_STOCK = "in_stock";
const PARAM_MIN = "min";
const PARAM_MAX = "max";
const PARAM_SORT = "sort";

/** Every facet key (NOT `q` — clearing filters must not clear the search). */
const ALL_FILTER_PARAMS = [
  PARAM_CATEGORY,
  PARAM_MARKET,
  PARAM_IN_STOCK,
  PARAM_MIN,
  PARAM_MAX,
  PARAM_SORT,
] as const;

/** Count threshold above which the page shows the "mass" label (story AC #7). */
export const RESULT_COUNT_MASS_THRESHOLD = 1000;

/** No public markets-list endpoint in this story's deps; same as MarketplacePage. */
const STATIC_MARKET_OPTIONS: readonly MarketOption[] = [];

function parseUrlPrice(raw: string | null): number | null {
  if (raw === null) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

export interface ParsedSearchUrl {
  q: string;
  categoryId: string | null;
  marketIds: string[];
  inStockOnly: boolean;
  priceRange: PriceRangeValue;
  sort: SortValue;
}

/** Parse the current URL search params into the in-memory search state. */
export function parseSearchUrl(params: URLSearchParams): ParsedSearchUrl {
  const sortParam = params.get(PARAM_SORT);
  // Cap at the backend's max so a hand-edited long `q` URL never 422s.
  const rawQ = params.get(PARAM_Q) ?? "";
  return {
    q: rawQ.slice(0, SEARCH_Q_MAX_LEN),
    categoryId: params.get(PARAM_CATEGORY),
    marketIds: params.getAll(PARAM_MARKET),
    inStockOnly: params.get(PARAM_IN_STOCK) === "true",
    priceRange: {
      min: parseUrlPrice(params.get(PARAM_MIN)),
      max: parseUrlPrice(params.get(PARAM_MAX)),
    },
    sort: isSortValue(sortParam) ? sortParam : "relevance",
  };
}

export function SearchResultsPage(): ReactElement {
  const { t } = useTranslation("marketplace");
  const { reduced } = useGardenMotion();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sheetOpen, setSheetOpen] = useState<boolean>(false);
  // Restore focus to the trigger when the filter Sheet closes (a11y).
  const filtersTriggerRef = useRef<HTMLButtonElement | null>(null);
  const openSheet = useCallback((): void => setSheetOpen(true), []);
  const closeSheet = useCallback((): void => {
    setSheetOpen(false);
    filtersTriggerRef.current?.focus();
  }, []);

  const parsed = useMemo(() => parseSearchUrl(searchParams), [searchParams]);
  const trimmedQuery = parsed.q.trim();
  const hasQuery = trimmedQuery.length > 0;

  const filters: SearchFilters = useMemo(
    () => ({
      q: trimmedQuery,
      categoryId: parsed.categoryId,
      marketIds: parsed.marketIds,
      inStockOnly: parsed.inStockOnly,
      minPrice: parsed.priceRange.min,
      maxPrice: parsed.priceRange.max,
      sort: parsed.sort,
    }),
    [trimmedQuery, parsed],
  );

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMarketplaceSearch(filters);

  const items: ProductSearchItem[] = useMemo(
    () => flattenSearchPages(data),
    [data],
  );
  // SCRUM-184 — `total_estimate` is the PLANNER's row guess (EXPLAIN "Plan
  // Rows"; Addendum §6.4 forbids COUNT(*) on the latency budget), not a real
  // count, and it routinely disagrees with reality: a search matching zero
  // rows still reported 9. Printing it verbatim produced the reported
  // contradiction — "3 products" sitting directly above "Nothing found".
  //
  // ADR-050 supersedes the plain-count AC #6/#7 wording FOR SEARCH ONLY: a
  // null `next_cursor` on this path does not prove completeness (search.sql's
  // per-arm LIMIT 40 candidate cap sits before the outer visibility/structured
  // filters — see `countLabel` below), so search never claims an exact count
  // in any state. The zero-mask rule (never claim a count over an empty grid)
  // is unchanged.
  const rawEstimate = data?.pages[0]?.total_estimate ?? null;

  // Keep one URL param in sync; the filters flow through the query key so a
  // change re-issues from page 1 and the grid is REPLACED (AC #4).
  const applyParams = useCallback(
    (mutate: (next: URLSearchParams) => void): void => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleCategorySelect = useCallback((_categoryId: string): void => {
    // CategoryTree writes `?category=<id>` to the URL itself; nothing else to do
    // here (the URL change flows into the query key and refetches).
  }, []);

  const handleCategoryReset = useCallback((): void => {
    applyParams((next) => next.delete(PARAM_CATEGORY));
  }, [applyParams]);

  const handleMarketChange = useCallback(
    (ids: string[]): void => {
      applyParams((next) => {
        next.delete(PARAM_MARKET);
        for (const id of ids) next.append(PARAM_MARKET, id);
      });
    },
    [applyParams],
  );

  const handleInStockChange = useCallback(
    (checked: boolean): void => {
      applyParams((next) => {
        if (checked) next.set(PARAM_IN_STOCK, "true");
        else next.delete(PARAM_IN_STOCK);
      });
    },
    [applyParams],
  );

  const handlePriceChange = useCallback(
    (value: PriceRangeValue): void => {
      applyParams((next) => {
        if (value.min !== null) next.set(PARAM_MIN, String(value.min));
        else next.delete(PARAM_MIN);
        if (value.max !== null) next.set(PARAM_MAX, String(value.max));
        else next.delete(PARAM_MAX);
      });
    },
    [applyParams],
  );

  const handleSortChange = useCallback(
    (value: SortValue): void => {
      applyParams((next) => {
        if (value === "relevance") next.delete(PARAM_SORT);
        else next.set(PARAM_SORT, value);
      });
    },
    [applyParams],
  );

  /** Clears the facets but KEEPS the query — the search itself is not a filter. */
  const handleClearAll = useCallback((): void => {
    applyParams((next) => {
      for (const key of ALL_FILTER_PARAMS) next.delete(key);
    });
  }, [applyParams]);

  const handleLoadMore = useCallback((): void => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const handleRetry = useCallback((): void => {
    void refetch();
  }, [refetch]);

  // Same rule as browse: a failed search raises a toast that carries the retry.
  useEffect(() => {
    if (!isError) return;
    notify.error(t("search.loadError"), {
      id: "marketplace-search-error",
      description: t("browse.loadErrorHint"),
      action: { label: t("browse.retry"), onClick: handleRetry },
    });
  }, [isError, t, handleRetry]);

  const renderSidebar = (ariaLabel: string): ReactElement => (
    <CategoryFilterSidebar
      ariaLabel={ariaLabel}
      marketOptions={STATIC_MARKET_OPTIONS}
      selectedMarketIds={parsed.marketIds}
      onMarketChange={handleMarketChange}
      inStockOnly={parsed.inStockOnly}
      onInStockChange={handleInStockChange}
      priceRange={parsed.priceRange}
      onPriceChange={handlePriceChange}
      sort={parsed.sort}
      onSortChange={handleSortChange}
      activeCategoryId={parsed.categoryId}
      onCategorySelect={handleCategorySelect}
      onCategoryReset={handleCategoryReset}
      // SCRUM-184 — the category tree is a static list of EVERY category and
      // the search response carries nothing to scope it by, so on results it
      // listed all twelve whatever was typed. Hidden until a real facet exists.
      showCategories={false}
    />
  );

  // The result-count label: "mass" copy above the threshold, approximate
  // pluralised count below it (STORY-003-008 AC #6/#7 superseded in
  // presentation — ADR-050, garden-backlog/ADR-LOG.md). Only rendered once a
  // count is known.
  const countLabel = useMemo<string | null>(() => {
    if (items.length === 0) return null; // SCRUM-184 zero-mask (001f6f4) — unchanged
    if (rawEstimate === null) return null;
    // SEARCH NEVER CLAIMS EXACTNESS (ADR-050): search.sql's per-arm LIMIT 40
    // candidate caps mean a null next_cursor does not prove the catalogue
    // holds no more matches. Approximate wording always; the floor stops the
    // planner's guess undercutting what is on screen.
    const floor = Math.max(rawEstimate, items.length);
    if (floor >= RESULT_COUNT_MASS_THRESHOLD) {
      return t("search.resultCountMass", { count: RESULT_COUNT_MASS_THRESHOLD });
    }
    return t("search.resultCountApprox", { count: floor });
  }, [items.length, rawEstimate, t]);

  const showEmpty = hasQuery && !isLoading && !isError && items.length === 0;

  return (
    <main className="mx-auto flex w-full max-w-screen-xl flex-col gap-5 px-4 py-5 sm:py-6">
      <SlipHeader
        title={
          hasQuery ? t("search.title", { query: parsed.q }) : t("search.titleEmpty")
        }
        titleTestId="search-title"
        entranceKey={trimmedQuery}
        count={countLabel}
        countTestId="result-count"
        countLive
        actions={
          hasQuery ? (
            <button
              ref={filtersTriggerRef}
              type="button"
              onClick={openSheet}
              data-testid="filters-open"
              aria-haspopup="dialog"
              aria-expanded={sheetOpen}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-[10px] border border-[color:var(--g-rule-strong)] px-3 text-sm font-semibold text-foreground transition-colors hover:border-[color:var(--g-primary)] hover:text-[color:var(--g-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background lg:hidden"
            >
              <SlidersHorizontal {...iconProps("sm")} />
              {t("filters.open")}
            </button>
          ) : undefined
        }
      >
        {hasQuery ? (
          <AppliedFilters
            categoryId={parsed.categoryId}
            onClearCategory={handleCategoryReset}
            marketOptions={STATIC_MARKET_OPTIONS}
            marketIds={parsed.marketIds}
            onMarketChange={handleMarketChange}
            inStockOnly={parsed.inStockOnly}
            onInStockChange={handleInStockChange}
            priceRange={parsed.priceRange}
            onPriceChange={handlePriceChange}
            sort={parsed.sort}
            onSortChange={handleSortChange}
            onClearAll={handleClearAll}
          />
        ) : null}
      </SlipHeader>

      {!hasQuery ? (
        // No query yet: a blank slip with the motif at real presence, matching
        // the empty-results surface rather than inventing a third look.
        <MotifSurface
          tone="primary"
          opacity={0.14}
          className="rounded-[var(--g-radius)] border border-[color:var(--g-rule)] bg-card text-card-foreground"
        >
          <motion.div
            role="status"
            data-testid="search-prompt"
            className="flex flex-col gap-2 p-5 sm:p-6"
            initial={reduced ? false : { opacity: 0, y: 8 }}
            animate={reduced ? undefined : { opacity: 1, y: 0 }}
            transition={
              reduced
                ? undefined
                : { duration: MOTION.dur.bubble, ease: MOTION.ease.out }
            }
          >
            <p className="max-w-prose text-sm leading-relaxed text-[color:var(--g-ink-2)]">
              {t("search.prompt")}
            </p>
          </motion.div>
        </MotifSurface>
      ) : (
        <div className="flex gap-8">
          <div
            className="hidden w-60 shrink-0 lg:block xl:w-64"
            data-testid="sidebar-desktop"
          >
            <div className="sticky top-4">{renderSidebar(t("aria.filters"))}</div>
          </div>

          <div className="min-w-0 flex-1">
            {isError ? (
              <LoadError
                message={t("search.loadError")}
                onRetry={handleRetry}
                testId="search-error"
                retryTestId="search-retry"
              />
            ) : showEmpty ? (
              <EmptyState variant="search" />
            ) : (
              <>
                {/* Entrance re-runs only on a NEW query (entranceKey); appends
                    from "Load more" do not re-stagger. */}
                <ProductGrid
                  items={items}
                  isLoading={isLoading}
                  entranceKey={trimmedQuery}
                />
                {hasNextPage && (
                  <div className="mt-8 flex justify-center border-t border-[color:var(--g-rule)] pt-6">
                    <button
                      type="button"
                      onClick={handleLoadMore}
                      disabled={isFetchingNextPage}
                      data-testid="load-more"
                      className="inline-flex min-h-[44px] items-center rounded-[10px] border border-[color:var(--g-rule-strong)] px-6 text-sm font-semibold text-foreground transition-colors hover:border-[color:var(--g-primary)] hover:text-[color:var(--g-primary)] disabled:pointer-events-none disabled:text-[color:var(--g-ink-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {isFetchingNextPage
                        ? t("browse.loadingMore")
                        : t("browse.loadMore")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <FilterSheet open={sheetOpen} onClose={closeSheet}>
        {renderSidebar(t("filters.title"))}
      </FilterSheet>
    </main>
  );
}

export default SearchResultsPage;
